import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { hiveLabel } from "../../services/hiveActions";
import { Field } from "./Field";

/**
 * Choose a hive by name (#211).
 *
 * Replaces the "Hive ID" text boxes scattered through the app, which asked
 * the user to type a primary key. That was not merely inconvenient -- it
 * was wrong in a way that loses data. hive_id is NOT the number shown
 * anywhere in the UI: users see hive_number, which restarts at 1 per site
 * (#173). Someone confidently typing "3" for "Hive 3" therefore hits a
 * different hive entirely, or a foreign-key error, with nothing in the form
 * to catch it.
 *
 * Deliberately NOT built on SelectOrAdd: that control is for user-defined
 * vocabulary lists, where inventing a new value is the point. Hives are
 * real records, and "add new" here would mean creating a hive from inside
 * an unrelated form.
 *
 * Loads its own list rather than taking one as a prop, because the callers
 * are five unrelated forms that otherwise each grow their own fetch. Pass
 * `hives` explicitly on a page that already has them.
 */
export function HivePicker({ label = "Hive", value, onChange, hives: provided, required = false, includeBlank = true, siteInLabel = true }) {
  const [hives, setHives] = useState(provided || []);
  const [loading, setLoading] = useState(!provided);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (provided) {
      setHives(provided);
      return;
    }
    let cancelled = false;
    hivesApi
      .list({ status: "Active" })
      .then((r) => {
        if (cancelled) return;
        setHives(r.hives || []);
      })
      // A failed load must not look like "you have no hives" -- that reads
      // as data loss to someone who knows they have twenty.
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [provided]);

  const hint = failed
    ? "Couldn't load your hives. Refresh and try again."
    : !loading && hives.length === 0
      ? "No active hives yet."
      : undefined;

  return (
    <Field label={label} required={required} hint={hint}>
      {(id, aria) => (
        <select
          id={id}
          {...aria}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          disabled={loading || failed}
          className="touch-target"
        >
          {includeBlank ? <option value="">{loading ? "Loading..." : "Select a hive..."}</option> : null}
          {hives.map((h) => (
            // The label is what the user recognises; the VALUE is the id
            // they were previously expected to know by heart.
            <option key={h.hive_id} value={h.hive_id}>
              {/* The yard is normally part of the name, because
                  hive_number restarts at 1 per site (#173) and "Hive 1"
                  alone is ambiguous across yards.
                  
                  siteInLabel={false} drops it for a caller that has
                  already narrowed the list to ONE site -- there the
                  prefix is the same words on every row, which is noise
                  that pushes the number that actually differs off to the
                  right. Reported on the harvest form. */}
              {siteInLabel ? hiveLabel(h) : hiveLabel({ hive_number: h.hive_number })}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
